import { createStore, useStore } from 'zustand';

import { normalizeSearchQuery } from '../model';

type DirectorySearchState = {
  /** The box's text as typed. It runs ahead of `searchQuery` for a moment while typing. */
  searchInput: string;
  /** What the experts list is fetched with: the typed text once the typing paused or was submitted. */
  searchQuery: string;
};

type DirectorySearchActions = {
  setSearchInput: (searchInput: string) => void;
  /** Applies the text as the query, without the spaces around it. */
  applySearch: (typed: string) => void;
  reset: () => void;
};

export type DirectorySearchStore = DirectorySearchState & DirectorySearchActions;

export const INITIAL_DIRECTORY_SEARCH: DirectorySearchState = {
  searchInput: '',
  searchQuery: '',
};

export const createDirectorySearchStore = () =>
  createStore<DirectorySearchStore>()((set) => ({
    ...INITIAL_DIRECTORY_SEARCH,
    setSearchInput: (searchInput) => set({ searchInput }),
    applySearch: (typed) => set({ searchQuery: normalizeSearchQuery(typed) }),
    reset: () => set(INITIAL_DIRECTORY_SEARCH),
  }));

/**
 * The directory's search text and the query applied from it.
 *
 * The text lives here rather than in the screen so that typing re-renders the search box and
 * nothing else. The screen reads only `searchQuery`, which changes once per search, instead of
 * rebuilding its whole list on every keystroke.
 */
export const directorySearchStore = createDirectorySearchStore();

let holders = 0;

/**
 * Held by each mounted directory screen. The store outlives any one screen and the directory
 * backs two routes, so a screen that unmounts after another mounted must not clear the text the
 * live one shows. The last release resets the search. The returned release is safe to call twice.
 */
export function retainDirectorySearch(): () => void {
  holders += 1;

  let released = false;

  return () => {
    if (released) {
      return;
    }

    released = true;

    holders -= 1;

    if (holders === 0) {
      directorySearchStore.getState().reset();
    }
  };
}

/** Reads one slice of the directory search. Select narrowly; never the whole store. */
export function useDirectorySearchState<Selected>(
  selector: (state: DirectorySearchStore) => Selected
) {
  return useStore(directorySearchStore, selector);
}
